import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
  useCallback,
} from "react";
import {
  canAccessTicket,
  TICKET_LOCAL_UPDATE_EVENT,
  TICKET_LOCAL_DELETE_EVENT,
} from "../../utils/ticketUtils";

import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import Paper from "@material-ui/core/Paper";

import TicketListItem from "../TicketListItemCustom";
import TicketsListSkeleton from "../TicketsListSkeleton";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";

// Tempo mínimo em segundo plano para disparar ressincronização ao voltar.
// Evita refetch em alt-tabs rápidos; abaixo disso o socket raramente morre.
const MIN_HIDDEN_TIME_MS = 5000;

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  ticketsList: {
    flex: 1,
    maxHeight: "100%",
    overflowY: "scroll",
    ...theme.scrollbarStyles,
    borderTop: "2px solid rgba(0, 0, 0, 0.12)",
  },

  ticketsListHeader: {
    color: "rgb(67, 83, 105)",
    zIndex: 2,
    backgroundColor: "white",
    borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },

  ticketsCount: {
    fontWeight: "normal",
    color: "rgb(104, 121, 146)",
    marginLeft: "8px",
    fontSize: "14px",
  },

  noTicketsText: {
    textAlign: "center",
    color: "rgb(104, 121, 146)",
    fontSize: "14px",
    lineHeight: "1.4",
  },

  noTicketsTitle: {
    textAlign: "center",
    fontSize: "16px",
    fontWeight: "600",
    margin: "0px",
  },

  noTicketsDiv: {
    display: "flex",
    height: "100px",
    margin: 40,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === "RESET_UNREAD") {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticket.id);

    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state.splice(ticketIndex, 1);
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const TicketsListCustom = (props) => {
  const {
    status,
    searchParam,
    tags,
    users,
    showAll,
    selectedQueueIds,
    updateCount,
    style,
    onStatusChange,
  } = props;
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [ticketsList, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user;

  // Descarta o estado atual e refaz o fetch da página 1. Usado para
  // ressincronizar quando eventos de socket podem ter sido perdidos
  // (reconexão ou retorno do segundo plano).
  const refreshList = useCallback(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setRefreshKey((prev) => prev + 1);
  }, []);

  // Refs para o socket ler sempre o estado atual sem reconectar a cada render.
  const userRef = useRef(user);
  userRef.current = user;
  const selectedQueueIdsRef = useRef(selectedQueueIds);
  selectedQueueIdsRef.current = selectedQueueIds;
  const showAllRef = useRef(showAll);
  showAllRef.current = showAll;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [status, searchParam, dispatch, showAll, tags, users, selectedQueueIds]);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    queueIds: JSON.stringify(selectedQueueIds),
    refreshKey,
  });

  useEffect(() => {
    const queueIds = queues.map((q) => q.id);
    const filteredTickets = tickets.filter(
      (t) => queueIds.indexOf(t.queueId) > -1
    );

    if (profile === "user") {
      dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
    } else {
      dispatch({ type: "LOAD_TICKETS", payload: tickets });
    }
  }, [tickets, status, searchParam, queues, profile]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    const shouldUpdateTicket = (ticket) =>
      canAccessTicket(ticket, userRef.current, selectedQueueIdsRef.current) &&
      (!ticket.userId || ticket.userId === userRef.current.id || showAllRef.current);
    const notBelongsToUserQueues = (ticket) =>
      ticket.queueId && selectedQueueIdsRef.current.indexOf(ticket.queueId) === -1;

    // Aplica uma atualização de ticket vinda do socket ou do evento local.
    // Diferente do socket (onde o servidor já roteia por sala de status),
    // o evento local chega a todas as listas, então o status precisa ser
    // validado aqui: ticket que não pertence mais a esta lista é removido.
    const handleTicketUpdate = (ticket) => {
      const belongsToList = !status || ticket.status === status;
      if (
        belongsToList &&
        canAccessTicket(ticket, userRef.current, selectedQueueIdsRef.current) &&
        !notBelongsToUserQueues(ticket)
      ) {
        dispatch({ type: "UPDATE_TICKET", payload: ticket });
      } else {
        dispatch({ type: "DELETE_TICKET", payload: ticket.id });
      }
    };

    // "connect" dispara na conexão inicial e em toda reconexão. Se o
    // connectionStateRecovery do servidor recuperou a sessão (quedas de até
    // 2 min), socket.recovered=true: salas restauradas e eventos perdidos
    // reenviados, sem necessidade de refetch. Fora dessa janela, o estado
    // local pode estar obsoleto: refaz o fetch para ressincronizar (apenas
    // na reconexão; o fetch inicial já é feito pelo useTickets).
    let hasConnectedBefore = false;
    socket.on("connect", () => {
      if (status) {
        socket.emit("joinTickets", status);
      } else {
        socket.emit("joinNotification");
      }
      if (hasConnectedBefore && !socket.recovered) {
        refreshList();
      }
      hasConnectedBefore = true;
    });

    socket.on(`company-${companyId}-ticket`, (data) => {
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      if (data.action === "update") {
        handleTicketUpdate(data.ticket);
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    });

    // Atualização local (mesma aba): disparada após aceitar/resolver/devolver
    // um ticket, com o payload retornado pelo PUT. Garante que as listas
    // reflitam a mudança mesmo se o socket estiver morto naquele momento.
    const handleLocalTicketUpdate = (e) => handleTicketUpdate(e.detail);
    window.addEventListener(TICKET_LOCAL_UPDATE_EVENT, handleLocalTicketUpdate);

    const handleLocalTicketDelete = (e) =>
      dispatch({ type: "DELETE_TICKET", payload: e.detail });
    window.addEventListener(TICKET_LOCAL_DELETE_EVENT, handleLocalTicketDelete);

    socket.on(`company-${companyId}-appMessage`, (data) => {
      const loggedUser = userRef.current;
      const queueIds = loggedUser.queues.map((q) => q.id);
      if (
        loggedUser.profile === "user" &&
        (queueIds.indexOf(data.ticket.queue?.id) === -1 ||
          data.ticket.queue === null)
      ) {
        return;
      }

      if (data.action === "create" && shouldUpdateTicket(data.ticket)) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
      }
    });

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    });

    return () => {
      window.removeEventListener(
        TICKET_LOCAL_UPDATE_EVENT,
        handleLocalTicketUpdate
      );
      window.removeEventListener(
        TICKET_LOCAL_DELETE_EVENT,
        handleLocalTicketDelete
      );
      socket.disconnect();
    };
    // Reconecta apenas quando a aba (status) muda. Filtros de fila, showAll e
    // usuário são lidos via ref, mantendo a conexão estável e sem perder
    // eventos. refreshList é estável (useCallback sem deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Ao voltar do segundo plano, a conexão socket pode ter morrido sem o
  // cliente perceber ainda (janela "zumbi" de até ~45s no socket.io v3),
  // deixando as listas obsoletas. Refaz o fetch se a aba ficou oculta por
  // tempo suficiente para isso ser um risco real.
  const hiddenSinceRef = useRef(null);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now();
        return;
      }
      if (
        hiddenSinceRef.current &&
        Date.now() - hiddenSinceRef.current >= MIN_HIDDEN_TIME_MS
      ) {
        refreshList();
      }
      hiddenSinceRef.current = null;
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshList]);

  useEffect(() => {
    const count = ticketsList.length;
    if (typeof updateCount === "function") {
      updateCount(count);
    }
  }, [ticketsList.length, updateCount]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const handleTicketClick = () => {
    if (onStatusChange) {
      onStatusChange("open"); // Muda para aba "Atendendo"
    }
  };

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name="closed"
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t("ticketsList.noTicketsTitle")}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t("ticketsList.noTicketsMessage")}
              </p>
            </div>
          ) : (
            <>
              {ticketsList.map((ticket) => (
                <TicketListItem ticket={ticket} key={ticket.id} onTicketClick={handleTicketClick} />
              ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListCustom;
